// Sticky-scroll gallery (ui-layout style).
//
// Layout:
//   ┌──────────────┬───────────────┐
//   │   Hero       │   Col 1 tiles │  ← Hero is `sticky top-0` and stays
//   │  (sticky)    │   Col 2 tiles │    pinned while the right side scrolls
//   │              │   Col 3 tiles │    past it. Each tile inside a column
//   └──────────────┴───────────────┘    uses `sticky` so they stack on scroll.
//
// On mobile we collapse to a single-column flow with the hero on top.
// Wraps content in <ReactLenis root> for smooth, momentum-based scrolling.
import { ReactLenis } from "lenis/react";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface StickyScrollProps {
  /** Sticky hero shown on the left (desktop) or top (mobile). */
  hero: ReactNode;
  /** Tiles to flow through the sticky columns. Distributed round-robin. */
  tiles: ReactNode[];
  /** Optional pinned footer card after the columns finish. */
  footer?: ReactNode;
  /** Number of columns on desktop (default 3). */
  columns?: 2 | 3;
  className?: string;
}

const StickyScroll = forwardRef<HTMLDivElement, StickyScrollProps>(
  ({ hero, tiles, footer, columns = 3, className }, ref) => {
    // Distribute tiles round-robin across columns so they stay visually
    // balanced regardless of total count.
    const cols: ReactNode[][] = Array.from({ length: columns }, () => []);
    tiles.forEach((t, i) => cols[i % columns].push(t));

    const colsClass = columns === 2 ? "md:grid-cols-2" : "md:grid-cols-3";

    return (
      <ReactLenis root options={{ duration: 1.1, smoothWheel: true }}>
        <div ref={ref} className={cn("relative w-full", className)}>
          {/* HERO + COLUMNS — hero takes ~2/5, tiles take ~3/5 so the
              right-side gallery has real estate to breathe at md+. */}
          <section className="relative grid grid-cols-1 gap-6 md:grid-cols-5 md:gap-8">
            {/* Hero — pinned on desktop, stacked on top on mobile */}
            <div className="md:col-span-2 md:sticky md:top-0 md:flex md:h-screen md:items-center">
              <div className="w-full">{hero}</div>
            </div>

            {/* Right side — sticky columns. Span 3/5 of the row at md+. */}
            <div
              className={cn(
                "grid grid-cols-2 gap-3 md:col-span-3 md:gap-4",
                colsClass,
              )}
            >
              {cols.map((colTiles, ci) => (
                <div key={ci} className="flex flex-col gap-3 md:gap-4">
                  {colTiles.map((tile, i) => (
                    <figure
                      key={i}
                      className="sticky"
                      style={{
                        // Stagger the pin offset so tiles don't all stop at
                        // the exact same line — creates the classic stacked
                        // reveal as you scroll.
                        top: `${(i % 8) * 12 + 16}px`,
                      }}
                    >
                      {tile}
                    </figure>
                  ))}
                </div>
              ))}
            </div>
          </section>

          {/* Footer — pinned card after the gallery finishes */}
          {footer && (
            <section className="relative mt-10 md:mt-16">
              <div className="md:sticky md:top-8 md:flex md:min-h-[40vh] md:items-center md:justify-center">
                {footer}
              </div>
            </section>
          )}
        </div>
      </ReactLenis>
    );
  },
);

StickyScroll.displayName = "StickyScroll";

export default StickyScroll;
